import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import {
  Button,
  DropdownNext,
  DropdownNextProps,
  Icon,
  Text,
  useTheme,
  utils,
} from '@desygner/ui-common-components';
import { useTranslation } from '@desygner/ui-common-translation';

import { AccessSwitchMode, ShareAccessLevel } from '../ShareAccess.types';
import { getAccessDataMap } from '../config/share-access.config';
import { isFeatureEnabled } from '../share-access.utils';

const StyledWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => utils.rem(theme.Spacing.Size.XSmall)};

  ${({ theme }) => css`
    @media (max-width: ${theme.Screen.Size.XSmall}px) {
      flex-direction: column;
      gap: ${utils.rem(theme.Spacing.Size.Small)};
    }
  `};
`;

const StyledButtonWrapper = styled.div`
  button {
    width: 100%;
  }

  ${({ theme }) => css`
    @media (max-width: ${theme.Screen.Size.XSmall}px) {
      width: 100%;
    }
  `};
`;

const StyledSelectWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => utils.rem(theme.Spacing.Size.XSmall)};
  border: ${({ theme }) =>
    `${utils.rem(theme.Border.Size.Small)} solid
    ${theme.Color.Neutrals.Border}`};
  padding: 0 ${({ theme }) => utils.rem(theme.Spacing.Size.Small)};
  border-radius: ${({ theme }) => utils.rem(theme.Border.Radius.Small)};
  cursor: pointer;
  transition: ${({ theme }) => `border-color ${theme.Transition.Timing.Out};`};

  &:hover {
    border-color: ${({ theme }) => theme.Color.Brand.Primary};
    transition: ${({ theme }) => `border-color ${theme.Transition.Timing.In};`};
  }

  ${({ theme }) => css`
    @media (max-width: ${theme.Screen.Size.XSmall}px) {
      padding: ${utils.rem(theme.Spacing.Size.Small)};
    }
  `};
`;

const StyledSelectTextWrapper = styled.div`
  flex: 1;
`;

export type CopyLinkProps = {
  features?: Partial<Record<ShareAccessLevel, boolean>>;
  selectedAccess: ShareAccessLevel;
  accessSwitchMode?: AccessSwitchMode;
  onButtonClick: () => void;
  onSelectedAccessChange: (access: ShareAccessLevel) => void;
  projectType?: 'design' | 'template';
};

export function CopyLink({
  features,
  onButtonClick,
  selectedAccess,
  onSelectedAccessChange,
  accessSwitchMode,
  projectType,
}: CopyLinkProps) {
  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const theme = useTheme();

  const isSwitchHidden = accessSwitchMode === AccessSwitchMode.Hidden;
  const isSwitchDisabled = accessSwitchMode === AccessSwitchMode.Disabled;

  const accessDataMap = useMemo(() => getAccessDataMap(projectType), [projectType]);

  const options = useMemo<DropdownNextProps['options']>(() => {
    return Object.values(accessDataMap)
      .filter((access) => isFeatureEnabled(access.id, features))
      .map<DropdownNextProps['options'][number]>((access) => ({
        label: t(access.labelKey),
        value: access.id,
        icon: { name: access.icon },
        isSelected: selectedAccess === access.id,
        description: t(access.subtitleKey),
        extra:
          selectedAccess === access.id ? (
            <Icon name={'check'} size={'Small'} color={theme.Color.Neutrals.Dark} />
          ) : undefined,
        disabled: isSwitchDisabled,
      }));
  }, [accessDataMap, selectedAccess, t, features, isSwitchDisabled, theme.Color.Neutrals.Dark]);

  if (isSwitchHidden) {
    return (
      <StyledButtonWrapper>
        <Button
          onClick={onButtonClick}
          icon={{
            name: 'link---1',
            color: theme.Color.Neutrals.Dark,
          }}
        >
          {t('shared.share_viewer_access.copy_link_text_button')}
        </Button>
      </StyledButtonWrapper>
    );
  }

  return (
    <DropdownNext
      state={{
        open: isSelectOpen,
      }}
      options={options}
      trigger="click"
      onOpenChange={(open) => setIsSelectOpen(open)}
      onClick={(value) => {
        onSelectedAccessChange(String(value) as ShareAccessLevel);
        setIsSelectOpen(false);
      }}
      width="matchButton"
      placement={'bottom'}
    >
      <StyledWrapper>
        <StyledSelectWrapper>
          <Icon
            name={accessDataMap[selectedAccess].icon}
            size={'Small'}
            color={theme.Color.Neutrals.Dark}
          />
          <StyledSelectTextWrapper>
            <Text size={'Body'} color={theme.Color.Neutrals.Dark}>
              {t(accessDataMap[selectedAccess].labelKey)}
            </Text>
          </StyledSelectTextWrapper>

          <Icon
            name={'chevron---down'}
            size={'Small'}
            angle={isSelectOpen ? 180 : 0}
            color={theme.Color.Neutrals.Dark}
          />
        </StyledSelectWrapper>

        <StyledButtonWrapper>
          <Button
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onButtonClick();
            }}
            icon={{
              name: 'link---1',
              color: theme.Color.Neutrals.Dark,
            }}
          >
            {t('shared.share_viewer_access.copy_link_text_button')}
          </Button>
        </StyledButtonWrapper>
      </StyledWrapper>
    </DropdownNext>
  );
}
